import type { QueryKey } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { env } from '@/config'

const RECONNECT_MIN_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 10000
// Cloud Run enforces a hard WebSocket connection timeout (up to 60 minutes),
// so an eventual server-initiated close is expected, not an error state —
// this loop just reconnects and resyncs when it happens.

function buildDisplayWebSocketUrl(weddingId: string, dt: string): string {
  const url = new URL(
    `/api/v1/weddings/${weddingId}/display/ws`,
    env.EVT_API_URL,
  )
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.searchParams.set('dt', dt)
  return url.toString()
}

/**
 * Subscribes a display route to realtime push for one wedding's activity
 * state, writing every message straight into the given React Query cache
 * entry (the same shape the REST display endpoint's response.data already
 * produces). Reconnects with backoff on drop and invalidates the query once
 * per successful (re)connect so a gap in the WS connection self-heals
 * rather than leaving a stale screen. No-ops while `dt` is empty — the
 * screen isn't authorized to open the socket yet.
 */
export function useDisplayWebSocket(options: {
  weddingId: string
  dt: string | null
  queryKey: QueryKey
}) {
  const { weddingId, dt, queryKey } = options
  const queryClient = useQueryClient()
  const queryKeyRef = useRef(queryKey)

  useEffect(() => {
    queryKeyRef.current = queryKey
  }, [queryKey])

  useEffect(() => {
    if (!dt) return

    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectDelay = RECONNECT_MIN_DELAY_MS
    let closedByEffect = false

    const connect = () => {
      socket = new WebSocket(buildDisplayWebSocketUrl(weddingId, dt))

      socket.onopen = () => {
        reconnectDelay = RECONNECT_MIN_DELAY_MS
        queryClient.invalidateQueries({ queryKey: queryKeyRef.current })
      }

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as { payload?: unknown }
          queryClient.setQueryData(
            queryKeyRef.current,
            (old: { success?: boolean; data?: unknown } | undefined) => ({
              success: old?.success ?? true,
              data: parsed.payload,
            }),
          )
        } catch {
          // Malformed frame — ignore, the next resync corrects any drift.
        }
      }

      socket.onclose = () => {
        if (closedByEffect) return
        reconnectTimer = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_DELAY_MS)
          connect()
        }, reconnectDelay)
      }

      socket.onerror = () => {
        socket?.close()
      }
    }

    connect()

    return () => {
      closedByEffect = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      socket?.close()
    }
  }, [weddingId, dt, queryClient])
}
